import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import styled from '@mui/system/styled';
import Chip from '@mui/material/Chip';
import theme from '@/mui/theme';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { getPittogramma } from '@/utils/pittogrammi';
import { sanitize } from '@/utils/miscellaneous';

interface ProductDetailInfoProps {
  iconList?: {
    icon: string;
    iconTooltip: string;
  }[];
  descriptionList: {
    title?: string;
    desc?: string;
    listDesc?: [
      {
        name: string;
        value: string;
      }
    ];
    tag?: string[];
  }[];
}

function isTitleToShow(elem: any) {
  let isTagConsistent = false;
  let isListDescConsistent = false;
  if (elem && elem !== undefined) {
    //check tag
    if (elem.tag && elem.tag !== undefined) {
      elem.tag.map((tag: string) => {
        tag !== undefined ? isTagConsistent = true : isTagConsistent = false;
      });
    }

    //check listDesc
    if (elem.listDesc && elem.listDesc !== undefined) {
      elem.listDesc.map((desc: string) => {
        desc !== undefined ? isListDescConsistent = true : isListDescConsistent = false;
      });
    }

    //check each
    if ((elem.desc && elem.desc !== undefined) || isTagConsistent || isListDescConsistent) {
      return true;
    }
    else {
      return false;
    }
  }

}

const StyledNameSpan = styled('span')({
  color: "#545454",
  fontSize: "15px",
  letterSpacing: "-0.2px",
  lineHeight: "24px",
  marginBottom: "16px",
  textAlign: 'left',
  fontWeight: "bold"
});

const StyledValueSpan = styled('span')({
  color: "#545454",
  fontSize: "15px",
  letterSpacing: "-0.2px",
  lineHeight: "24px",
  marginBottom: "16px",
  textAlign: 'left',
});

const StyledTitle = styled(Typography)({
  color: "#1288cf",
  fontSize: "18px",
  fontWeight: "bold",
  letterSpacing: "0",
  lineHeight: "21px",
  marginBottom: "5px",
  marginTop: "16px",
  textAlign: 'left'
});

const StyledDesc = styled(Typography)({
  color: "#545454",
  fontSize: "15px",
  letterSpacing: "-0.2px",
  lineHeight: "24px",
  textAlign: 'left',
});

const StyledListDesc = styled(Typography)({
  color: "#545454",
  fontSize: "15px",
  letterSpacing: "-0.2px",
  lineHeight: "24px",
  textAlign: 'left',
});

const StyledBoxIcon = styled(Box)({
  [theme.breakpoints.up('sm')]: {
    paddingLeft: '19px',
    paddingRight: '5vw',
  },
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: '11px',
});

const StyledBoxInfo = styled(Box)({
  [theme.breakpoints.up('sm')]: {
    paddingLeft: '19px',
  },
  display: 'flex',
  flexDirection: 'column',
});

const StyledChip = styled(Chip)({
  [theme.breakpoints.up('sm')]: {
    height: '32px'
  },
  borderRadius: '5px',
  border: '1px solid #606060',
  padding: '0 3.5px',
  margin: '8px 10px 0 0',
  color: '#606060',
  fontSize: '12px',
  fontWeight: 'bold',
  letterSpacing: '0',
  lineHeight: '20px',
  textAlign: 'center',
  height: '27px'
});

const HtmlTooltip = styled(({ className, ...rest }: TooltipProps) => (
  <Tooltip {...rest} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffffff',
    borderRadius: '5px',
    color: '#182641',
    maxWidth: '220px',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0',
    padding: '14px',
    boxShadow: '0 7px 20px 0 rgba(28, 28, 28, 0.43)',
    border: '0',
    '.MuiTooltip-arrow': {
      '&::before': {
        border: '0!important',
        backgroundColor: '#ffffff!important',
        boxShadow: 'unset!important',
        position: 'relative',
        marginBottom: '-1px',
        zIndex: '-99!important'
      }
    }
  },
})) as typeof Tooltip;

const StyledButton = styled(Button)({
  padding: '0',
  margin: '0 10px 10px 0',
  minWidth: '0',
  cursor: 'default',
  'svg': {
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'unset'
  }
});


const ProductDetailInfo: React.FC<ProductDetailInfoProps> = ({ iconList, descriptionList }) => {
  return <Box sx={{ marginTop: { sm: 0, xs: '39px' } }}>
    {iconList &&
      <StyledBoxIcon>
        {iconList.map((singleIcon, index) => {
          return <HtmlTooltip
            key={index}
            title={<div dangerouslySetInnerHTML={{__html: sanitize(singleIcon.iconTooltip)}} />}
            placement="top"
            arrow
          >
            <StyledButton>{getPittogramma(singleIcon.icon, singleIcon.iconTooltip)}</StyledButton>
          </HtmlTooltip>
        })}
      </StyledBoxIcon>
    }
    {descriptionList &&
      <StyledBoxInfo>
        {descriptionList.map((singleDescription, index) => {
          return <Box key={index}>
            {singleDescription.title && isTitleToShow(singleDescription) &&
              <StyledTitle>{singleDescription.title}</StyledTitle>
            }
            {singleDescription.desc &&
              <StyledDesc dangerouslySetInnerHTML={{__html: sanitize(singleDescription.desc)}} />
            }
            {singleDescription.listDesc &&
              singleDescription.listDesc.map((desc,index) => <StyledListDesc key={index}><StyledNameSpan dangerouslySetInnerHTML={{ __html: sanitize(desc.name + ": ") }} /><StyledValueSpan dangerouslySetInnerHTML={{ __html: sanitize(desc.value) }} /></StyledListDesc>)
            }
            {singleDescription.tag && singleDescription.tag.map((singleTag, index) => (
              <React.Fragment key={index}>
                {singleTag !== undefined &&
                  <StyledChip key={index} label={singleTag} variant="outlined"></StyledChip>
                }
              </React.Fragment>
            ))}
          </Box>
        })}
      </StyledBoxInfo>
    }
  </Box>
}

export default ProductDetailInfo;
